import type { ReactNode } from 'react';
import { Skeleton } from 'antd';
import type { UserResponse } from '../../../api/types';
import { useT } from '../../../locales';
import '../users.css';
import { UserStatusTag } from './UserStatusTag';
import { UsersTableActions } from './UsersTableActions';

const LOADING_PLACEHOLDERS = 3;

const INTERACTIVE_TARGET_SELECTOR = [
  'a',
  'button',
  'input',
  'select',
  'textarea',
  'summary',
  '[contenteditable="true"]',
  '[role="button"]',
  '[role="link"]',
  '[role="switch"]',
].join(', ');

function isInteractiveTarget(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest(INTERACTIVE_TARGET_SELECTOR) !== null;
}

function displayName(user: UserResponse): string {
  return [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email;
}

export interface UsersCardListProps {
  users: UserResponse[];
  loading?: boolean;
  empty?: ReactNode;
  canUpdate: boolean;
  canReadRoles: boolean;
  canDelete: boolean;
  onDetail: (userId: string) => void;
  onEdit: (user: UserResponse) => void;
  onManageRoles: (userId: string) => void;
  onDelete: (user: UserResponse) => void;
}

/**
 * Mobile (<576px) rendering of the users list.
 *
 * A seven-column table only fits a phone behind a horizontal scroll that hides
 * both the status and the row actions, so below the `sm` breakpoint the list
 * becomes one card per user: identity and status first, then the contact data,
 * the assigned roles and the same permission-gated actions as the table.
 */
export function UsersCardList({
  users,
  loading = false,
  empty,
  canUpdate,
  canReadRoles,
  canDelete,
  onDetail,
  onEdit,
  onManageRoles,
  onDelete,
}: UsersCardListProps) {
  const t = useT();

  if (loading && users.length === 0) {
    return (
      <ul className="users-card-list" aria-busy="true" aria-label={t('pages.users.title')}>
        {Array.from({ length: LOADING_PLACEHOLDERS }, (_, index) => (
          <li key={index} className="users-card-list__item">
            <div className="users-card users-card--placeholder">
              <Skeleton active title paragraph={{ rows: 3 }} />
            </div>
          </li>
        ))}
      </ul>
    );
  }

  if (users.length === 0) {
    return <>{empty}</>;
  }

  return (
    <ul
      className="users-card-list"
      aria-busy={loading || undefined}
      aria-label={t('pages.users.title')}
    >
      {users.map((user) => {
        const name = displayName(user);
        const meta = [user.username, user.department].filter(Boolean).join(' · ');

        return (
          <li key={user.id} className="users-card-list__item">
            <article
              className="users-card"
              onClick={(event) => {
                // The title button inside the card already opens the detail.
                if (!isInteractiveTarget(event.target)) {
                  onDetail(user.id);
                }
              }}
            >
              <div className="users-card__head">
                <button
                  type="button"
                  className="users-card__title"
                  onClick={() => onDetail(user.id)}
                >
                  {name}
                </button>
                <UserStatusTag isActive={user.isActive} />
              </div>
              <p className="users-card__email">{user.email}</p>
              <p className="users-card__meta">{meta || '-'}</p>
              {user.roles.length > 0 && (
                <ul className="users-card__roles">
                  {user.roles.map((role) => (
                    <li key={role.id} className="users-card__role">
                      {role.name}
                    </li>
                  ))}
                </ul>
              )}
              <div className="users-card__actions">
                <UsersTableActions
                  user={user}
                  canUpdate={canUpdate}
                  canReadRoles={canReadRoles}
                  canDelete={canDelete}
                  onDetail={onDetail}
                  onEdit={onEdit}
                  onManageRoles={onManageRoles}
                  onDelete={onDelete}
                />
              </div>
            </article>
          </li>
        );
      })}
    </ul>
  );
}
